/**
 * imageResolver — single source of truth for "give me a renderable image src".
 *
 * Outside demo mode this is essentially a pass-through to getImageAuthURLAsync
 * (object-storage sign / apikey append). Inside demo mode it implements a stale-while-
 * revalidate IndexedDB cache: every image is fetched once, stored as a blob,
 * and rendered from a `blob:` URL so subsequent loads are guaranteed offline.
 *
 * Three entry points share the same resolver core:
 *   - resolveImageSrc(rawUrl)            one-shot, used by <CachedImg>
 *   - subscribeImageSrc(rawUrl, onChange)  long-lived (carousels, CSS bg) with
 *                                          periodic re-resolve well below the
 *                                          AWS pre-sign TTL
 *   - useCachedImageSrc(rawUrl)          thin React hook over subscribeImageSrc
 */

import { useEffect, useState } from 'react';
import imageCache from './imageCache';
import { getImageAuthURLAsync } from 'components/main/common/utils';
import environment from 'utils/environment';
import { needsObjectStoragePresign } from 'services/awsUrlProcessor.service';

// Re-resolve cadence for long-lived subscribers. Must stay below the
// pre-sign TTL minus its safety buffer (1h - 5m today) so the URL is refreshed
// before it can possibly fail in-place.
export const REFRESH_INTERVAL_MS = 50 * 60 * 1000;

/**
 * Fetch a URL and persist the blob to imageCache (no-op outside demo mode).
 * On object-storage failure (network/non-2xx) retries once with a force-refreshed signature.
 * Always resolves; never throws.
 *
 * @returns {Promise<Blob|null>} the stored blob, or null on final failure
 */
async function fetchAndStore(authedUrl, rawUrl, { allowAwsRetry = true } = {}) {
  try {
    const response = await fetch(authedUrl);
    if (response.ok) {
      const blob = await response.blob();
      await imageCache.put(rawUrl, blob);
      return blob;
    }
    if (allowAwsRetry && needsObjectStoragePresign(rawUrl)) {
      const refreshed = await getImageAuthURLAsync(rawUrl, { forceRefresh: true });
      return await fetchAndStore(refreshed, rawUrl, { allowAwsRetry: false });
    }
  } catch (err) {
    if (allowAwsRetry && needsObjectStoragePresign(rawUrl)) {
      try {
        const refreshed = await getImageAuthURLAsync(rawUrl, { forceRefresh: true });
        return await fetchAndStore(refreshed, rawUrl, { allowAwsRetry: false });
      } catch (_) {
        // swallow; fall through to null
      }
    }
  }
  return null;
}

/**
 * One-shot resolution. Returns the string a consumer should put in `<img src>`
 * (or CSS `url(...)`), or null when nothing is available.
 *
 * Demo mode strategy is stale-while-revalidate:
 *   - cache hit  → return blob URL immediately, refresh in the background
 *   - cache miss → fetch synchronously, store, return blob URL (or null on
 *                  final failure)
 */
export async function resolveImageSrc(rawUrl) {
  if (!rawUrl) return null;

  const authedUrl = await getImageAuthURLAsync(rawUrl);

  if (!environment.isDemoMode()) {
    return authedUrl;
  }

  const cached = await imageCache.get(rawUrl);
  if (cached) {
    // Cache hit: serve blob only. Do not re-fetch on every <CachedImg> mount
    // (channel carousel remounts often) — that looked like logos "loading again".
    // Long-lived subscribers still refresh via subscribeImageSrc / REFRESH_INTERVAL_MS.
    return URL.createObjectURL(cached);
  }

  const blob = await fetchAndStore(authedUrl, rawUrl);
  if (blob) return URL.createObjectURL(blob);
  return null;
}

/**
 * Long-lived subscription: invokes `onChange(src)` whenever a re-resolve
 * produces a different src, and revokes any blob URL it superseded.
 * Safe to call repeatedly — each call returns its own unsubscribe fn.
 *
 * @returns {() => void} unsubscribe (clears the interval and revokes the
 *   currently-held blob URL if any)
 */
export function subscribeImageSrc(rawUrl, onChange) {
  let cancelled = false;
  let intervalId = null;
  let lastSrc = null;
  let lastBlobUrl = null;

  const tick = async () => {
    const src = await resolveImageSrc(rawUrl);
    if (cancelled) {
      if (src && src.startsWith('blob:')) URL.revokeObjectURL(src);
      return;
    }
    if (src === lastSrc) return;
    if (lastBlobUrl) URL.revokeObjectURL(lastBlobUrl);
    lastSrc = src;
    lastBlobUrl = src && src.startsWith('blob:') ? src : null;
    onChange(src);
  };

  tick();
  intervalId = setInterval(tick, REFRESH_INTERVAL_MS);

  return () => {
    cancelled = true;
    if (intervalId) clearInterval(intervalId);
    if (lastBlobUrl) URL.revokeObjectURL(lastBlobUrl);
  };
}

/**
 * React hook variant of subscribeImageSrc. Returns null until the first
 * resolution completes, then the resolved src.
 */
export function useCachedImageSrc(rawUrl) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!rawUrl) {
      setSrc(null);
      return undefined;
    }
    setSrc(null);
    const unsubscribe = subscribeImageSrc(rawUrl, setSrc);
    return unsubscribe;
  }, [rawUrl]);

  return src;
}

const imageResolver = {
  resolveImageSrc,
  subscribeImageSrc,
  useCachedImageSrc,
  REFRESH_INTERVAL_MS,
};

export default imageResolver;
